<template>
  <div class="timetable-employee-day">
    <header class="timetable-employee-day__row">
      <div class="timetable-employee-day__title">
        {{employee.name}}</div>
      <div class="timetable-employee-day__date">
        {{dateTitle}}
      </div>
    </header>
    <div v-if="isOffDay">Выходной</div>
    <body v-else>
      <div
        class="timetable-employee-day__row"
        ref="contextMenuTarget"
        v-bind:key="index"
        v-for="(slot, index) in slots">
        <button
          class="timetable-employee-day__row timetable-employee-day__item"
          type="button"
          v-bind:class="slot.tags"
          v-bind:disabled="isDisabled(slot)"
          v-on:click="onClickSlot(slot)"
          v-bind:style="getCellStyle(slot)"
          v-on:contextmenu.prevent="emitOpenContextMenu($event, index)">
          <div class="timetable-employee-day__item__times">
            <div class="timetable-employee-day__item__time">{{slot.starts_at}}</div>
            <div
              class="timetable-employee-day__item__time"
              v-for="time in slot.times"
              v-bind:key="time">{{ time }}</div>
          </div>
          <div class="timetable-employee-day__item__line" />
          <div
            class="timetable-employee-day__item__break"
            v-if="slot.tags.includes('break')">
            {{ getBreakTitle(slot) }}
          </div>
          <div
            class="timetable-employee-day__item__patient"
            v-else>
            {{slot.patient_name}}
          </div>
        </button>
      </div>

    </body>
  </div>
</template>

<script>
/**
 * @description Название события открытия записи на прием.
 * @type {string}
 */
const NAME_EVENT_OPEN_APPOINTMENT = 'open-appointment';

/**
 * @description Название события открытия перерыва.
 * @type {string}
 */
const NAME_EVENT_OPEN_BREAK = 'open-break';

/**
 * @description Название события открытия контекстного меню.
 * @type {string}
 */
const NAME_EVENT_OPEN_CONTEXT_MENU = 'open-context-menu';

/**
 * @description Расписание сотрудника на день.
 */
export default {
  computed: {
    /**
     * @description Выходной день.
     * @type {boolean}
     */
    isOffDay: ({ tags, slots }) => tags.includes('off') || !slots.length,
  },
  methods: {
    /**
     * @description Отправить событие открытия записи на прием.
     * @param {any} appointment Запись на прием.
     * @returns {void}
     */
    emitOpenAppointment(appointment) {
      this.$emit(NAME_EVENT_OPEN_APPOINTMENT, appointment);
    },
    /**
     * @description Отправить событие открытия перерыва.
     * @param {any} employeeBreak Перерыв.
     * @returns {void}
     */
    emitOpenBreak(employeeBreak) {
      this.$emit(NAME_EVENT_OPEN_BREAK, this.isoDate, this.employee, employeeBreak);
    },
    /**
     * @description Отправить событие открытия контекстного меню.
     * @returns {void}
     */
    emitOpenContextMenu(event, index) {
      const offset = {
        x: event.layerX,
        y: event.layerY,
      };
      const data = {
        offset,
        slot: this.slots[index],
        target: this.$refs.contextMenuTarget[index],
      };
      this.$emit(NAME_EVENT_OPEN_CONTEXT_MENU, data);
    },
    /**
     * @description Обработать клик по слоту.
     * @param {any} slot Слот.
     * @returns {void}
     */
    onClickSlot(slot) {
      if (slot.tags.includes('patient') || slot.tags.includes('open')) {
        this.emitOpenAppointment(slot);
      } else if (slot.tags.includes('break')) {
        const breakSlot = {
          ends_time: slot.ends_at,
          id: slot.id,
          note: slot.note,
          starts_time: slot.starts_at,
        };
        this.emitOpenBreak(breakSlot);
      }
    },
    isDisabled(slot) {
      if (slot.tags.includes('empty')) {
        return true;
      }
      return slot.tags.includes('break') && !this.hasEditBreaks;
    },
    /**
     * @description Получить заголовок слота перерыва.
     * @param {any} cell
     * @returns {string}
     */
    getBreakTitle(cell) {
      const text = 'Перерыв';
      return cell.note ? `${text}: ${cell.note}` : text;
    },
    /**
     * @description Получить стили слота.
     * @param {any} cell Слот.
     * @returns {any}
     */
    getCellStyle(cell) {
      const height = Math.max(1, cell.ratio_to_step || 1) * 42;
      return { height: `${height}px` };
    },
  },
  name: 'TimetableEmployeeDay',
  props: {
    /**
     * @description Дата.
     * @type {string}
     */
    dateTitle: { required: true, type: String },
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    isoDate: { required: true, type: String },
    /**
     * @description Возможность редактирования перерывов.
     * @type {boolean}
     */
    hasEditBreaks: { type: Boolean },
    /**
     * @description Сотрудник.
     * @type {{id:number,name:string}}
     */
    employee: { required: true, type: Object },
    /**
     * @description Слоты.
     * @type {any[]}
     */
    slots: { type: Array, default: () => [] },
    /**
     * @description Тэги.
     * @type {string[]}
     */
    tags: { type: Array, default: () => [] },
  },
};
</script>

<style lang="scss" scoped>
.timetable-employee-day {
  flex-direction: column;
  display: flex;
  height: 100%;
  flex-shrink: 0;
  width: 250px;
  border-left: 1px solid #eaeff3;
  padding: 18px 0 5px 18px;
  font-size: 12px;
  line-height: 14px;
  header {
    margin-bottom: 17px;
  }
  body {
    flex-direction: column;
    display: flex;
    gap: 2px;
    overflow-y: auto;
    overflow-x: none;
    padding-right: 20px;
  }
  &__title {
    color: var(--reading-color);
    font-weight: 600;
    font-size: 12px;
    line-height: 14px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  &__date {
    flex: 1;
    margin: 0px 20px 0 21px;
    color: var(--reading-color);
    font-weight: 300;
    font-size: 12px;
    line-height: 14px;
  }
  &__row {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
  }
  &__item {
    padding: 0 14px;
    gap: 17px;
    background-color: #eaeff3;
    border-radius: 3px;
    height: 42px;
    width: 100%;
    &.empty {
      &:hover {
        background-color: #eaeff3;
      }
    }
    &.break {
      text-align: left;
      background-color: var(--unfocus-color);
      .timetable-employee-day__item__time {
        color: var(--primary-background-color);
      }
      &:hover {
        background-color: var(--unfocus-color);
      }
    }
    &.open {
      .timetable-employee-day__item__patient {
        width: 100%;
        &::after {
          content: "＋";
          font-weight: 300;
          font-size: 12px;
          cursor: pointer;
        }
      }
    }
    &:hover {
      background-color: var(--primary-background-color);
      transition: background-color var(--animate-duration);
    }
    &__times {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      height: 100%;
    }
    &__time {
      color: #707683;
      font-size: 12px;
      font-weight: 500;
      line-height: 14px;
    }
    &__break {
      color: var(--primary-background-color);
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
    }
    &__patient {
      color: var(--reading-color);
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
    }
    &__line {
      border: 1px solid #c2cfe0;
      height: calc(100% - 7px);
    }
  }
}
@media screen and (max-width: 480px) {
  .timetable-employee-day {
    width: 100%;
    height: fit-content;
  }
}
</style>
